/*******************************************************************************
  VisualTour.js

  Author: Advait; 

*******************************************************************************/
import React, { useState, useEffect, useCallback, useRef } from "react";
import { API } from "aws-amplify";
import { processInspectionData } from "./helper/visualTour/processInspectionData";
import InspectionDetailsBox from "./InspectionDetailsBox";
import "../index.css";
import LocationDropdown from "./helper/visualTour/LocationDropdown";
import { useSelector } from "react-redux";
import downloadLocationData from "./helper/visualTour/DownloadLocationData";
import downloadIcon from "../assets/img/download.png";

function VisualTour({ siteid, sitename, selectedRound, onClose }) {
  const [inspectionData, setInspectionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [topMarginForElements, setTopMarginForElements] = useState(0);
  const [imageLoading, setImageLoading] = useState(true);
  const currentLocation = inspectionData.length > 0 ? inspectionData[currentIndex] : null;
  const isFirstLocation = currentIndex === 0;
  const isLastLocation =
    currentIndex === inspectionData.length - 1 || inspectionData.length === 0;

  // state to determine the status of inspection data box visual state
  const [inspectionDataVisibleState, setInspectionDataVisibleState] = useState(true);
  const isSysAdmin = useSelector((state) => state.user.sysAdmin);

  useEffect(() => {
    if (!siteid) return;
    setLoading(true);

    async function fetchInspectionData() {
      try {
        const response = await API.get(
          "fataskman",
          `/sites/${siteid}/inspectionrounddata/${selectedRound}`
        );
        const processedData = processInspectionData(response);
        setInspectionData(processedData);
      } catch (err) {
        console.error("Error fetching rounds:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchInspectionData();
  }, [siteid, selectedRound]);

  function handleLocationSelect(index) {
    if (currentIndex !== index) {
      setImageLoading(true);
      setCurrentIndex(index);
    }
  }

  function handleNext() {
    if (!isLastLocation) {
      setImageLoading(true);
      setCurrentIndex(currentIndex + 1);
    }
  }

  function handlePrev() {
    if (!isFirstLocation) {
      setImageLoading(true);
      setCurrentIndex(currentIndex - 1);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowRight") handleNext();
    if (e.key === "ArrowLeft") handlePrev();
  }

  const topBarRef = useRef(null);
  const overlayRef = useRef(null);
  const [isImageDownloading, setIsImageDownloading] = useState(false);
  // callback for both window + header measurements
  const updateMarginForDivElements = useCallback(() => {
    /**
     * We know the snapshot size will always be 16:9
     * we also know that the image will always take 100% browser width, so we can calculate height of image
     * Height of image = width of browser * 9 / 16.
     * If the Height of image + Height of header is < browser height, we know that there will be not be a scroll bar for image
     * Using this we can determine whether the buttons and text should vertically align, with respect to
     * height of image or height of browser window
     * 50 is subtracted from final value to compensate padding of elements.
     */
    const expectedImageHeight = (window.innerWidth * 9) / 16;
    const topBarHeight = topBarRef.current.offsetHeight;
    const browserWindowHeight = window.innerHeight;
    if (expectedImageHeight + topBarHeight < browserWindowHeight) {
      // set top margin based on image height
      setTopMarginForElements(expectedImageHeight / 2 - 50);
    } else {
      // from browser window height, subtract topBarHeight and set the margin as half of this value
      setTopMarginForElements((browserWindowHeight - topBarHeight) / 2 - 50);
    }
  }, []);

  /**
   * function responsible for tele-porting to a given location
   */
  async function teleportNow() {
    // Make API call and based on response decide whether to
    // open web-app in new window or not

    const response = await API.post("fataskman", "/sites/" + siteid, {
      body: {
        action: "teleport_to_location",
        args: {
          coords: currentLocation.coords,
        },
      },
    });
    const msg = response.message;

    if (!msg || (msg !== "available" && msg !== "in_session")) {
      alert(
        "Ava is currently not available for a telepresence session, please contact your administrator for more details."
      );
    } else if (msg === "available") {
      const url = `https://${response.hostname}/Ava8Web/session.htm?goToMap=${response.mapname}`;
      console.log(`url: ${url}`);
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }

  // Attach resize listener in the same callback
  const overlayTopBarRef = useCallback(
    (node) => {
      if (node) {
        topBarRef.current = node;
        updateMarginForDivElements(); // Initial measurement
        window.addEventListener("resize", updateMarginForDivElements);
      } else {
        window.removeEventListener("resize", updateMarginForDivElements); // Cleanup
      }
    },
    [updateMarginForDivElements]
  );

  const downloadImage = async () => {
    if (isImageDownloading) return;
    setIsImageDownloading(true);
    try {
      await downloadLocationData(currentLocation, sitename);
    } catch (err) {
      console.warn("downloadImage failed with error:", err);
    } finally {
      setIsImageDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="overlay" onKeyDown={handleKeyDown} tabIndex="0">
        <div className="overlay-data-loading">Loading inspection data...</div>
      </div>
    );
  }
  const inspectionDetailButtonText = inspectionDataVisibleState
    ? "Hide Details"
    : "Inspection Details";
  return (
    <div className="overlay" onKeyDown={handleKeyDown} tabIndex="0" ref={overlayRef}>
      <div className="overlay-topbar" ref={overlayTopBarRef}>
        <div className="overlay-topbar-inspection-data-button">
          <label
            className="overlay-topbar-inspection-data-button-label"
            onClick={() => setInspectionDataVisibleState(!inspectionDataVisibleState)}
          >
            {inspectionDetailButtonText}
          </label>
        </div>
        <div className="overlay-topbar-name">
          <LocationDropdown
            locations={inspectionData}
            onSelect={handleLocationSelect}
            currentLocationName={currentLocation ? currentLocation.name : ""}
          />
        </div>
        {isSysAdmin && currentLocation.coords && (
          <div className="overlay-topbar-teleport-now-button">
            {currentLocation.name && (
              <label className="overlay-topbar-teleport-now-label" onClick={teleportNow}>
                Teleport There Now
              </label>
            )}
          </div>
        )}
        <div className="overlay-topbar-rightcontrols">
          <div className="overlay-topbar-time">
            {currentLocation ? currentLocation.time : ""}
          </div>
          {currentLocation.url && (
            <button
              className="overlay-topbar-download-btn"
              onClick={downloadImage}
              disabled={isImageDownloading}
              aria-label="Download this image"
              title="Download this image"
            >
              <img src={downloadIcon} alt="Download" />
            </button>
          )}
        </div>
      </div>
      <div className="overlay-image-container">
        {inspectionData.length > 1 && (
          <>
            <button
              className={`nav-button left ${isFirstLocation ? "disabled" : ""}`}
              style={{ marginTop: `${topMarginForElements}px` }}
              onClick={handlePrev}
              disabled={isFirstLocation}
            >
              &#10094;
            </button>
            <button
              className={`nav-button right ${isLastLocation ? "disabled" : ""}`}
              style={{ marginTop: `${topMarginForElements}px` }}
              onClick={handleNext}
              disabled={isLastLocation}
            >
              &#10095;
            </button>
          </>
        )}
        {imageLoading && currentLocation?.url && (
          <div className="loader-container">
            <div className="loader" style={{ marginTop: `${topMarginForElements}px` }}></div>
          </div>
        )}
        {currentLocation?.url ? (
          <img
            className={`overlay-image ${imageLoading ? "loading" : ""}`}
            crossOrigin="anonymous"
            src={currentLocation.url}
            alt={""}
            onLoad={() => setImageLoading(false)}
            onError={() => setImageLoading(false)}
          />
        ) : (
          <div
            className="no-image-available"
            style={{ marginTop: `${topMarginForElements}px` }}
          >
            No image available
          </div>
        )}
        {(!imageLoading || !currentLocation?.url) && inspectionDataVisibleState && (
          <InspectionDetailsBox inspectionData={currentLocation?.inspection_details} />
        )}
      </div>
      <button className="overlay-close-btn" onClick={onClose}>
        End Tour
      </button>
    </div>
  );
}

export default VisualTour;
